import AddCriterionDialog from 'components/Rubrics/AddCriterionDialog';
import EditCriterionDialog from 'components/Rubrics/EditCriterionDialog';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import { cloneDeep, omit } from 'lodash';
import { PageContext } from 'pages/Preview/PageProvider';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Loading,
    Paper,
    ProgressBar,
    ResponsiveDialog,
    RichTextArea,
    RubricTable,
    Stack,
    styled,
    Text,
    TextArea,
} from 'shell/components';
import {
    AI_RUBRIC_MODIFICATION_URL,
    CUSTOM_CONTENT_WORD_COUNT_LIMIT,
    RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import {
    cancelPreview,
    getAIPreview,
    getChatCompletion,
    getHTMLWordCount,
    getWordCount,
    insertAtIndex,
    isValidRate,
} from 'utils/helper';
import { isUnscoredRubric, normalizeRubricCriteriaLevels } from 'utils/rubricScoring';
import { v4 as uuid } from 'uuid';

const defaultRubric = {
    criteria: [],
    sort: 'desc',
};

const defaultErrors = {
    criteria: {},
};

const FineTuneDialog = () => {
    const { course, currentAssignment, setPreviewSubmissions } = useApp();
    const { getAccount } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { device } = useViewport();
    const { onMessage } = useWebsocket();
    const { t } = useTranslation('assignment');

    const { previewSubmissions, setForm, fineTuneDialog, setFineTuneDialog } = useContext(PageContext);
    const isUnscored = isUnscoredRubric(currentAssignment);

    // form data
    const [prompt, setPrompt] = useState('');
    const [rubric, setRubric] = useState(defaultRubric);
    const [errors, setErrors] = useState(defaultErrors);
    const [gradingInstructions, setGradingInstructions] = useState('');
    const [feedbackInstructions, setFeedbackInstructions] = useState('');

    // rubric table
    const [createCriterionDialog, setCreateCriterionDialog] = useState({ open: false });
    const [editCriterionDialog, setEditCriterionDialog] = useState({ open: false });

    // requests-related states
    const [isRubricGenerating, setIsRubricGenerating] = useState(false);
    const [isPreviewGenerating, setIsPreviewGenerating] = useState(false);
    const [numPreviewsFinished, setNumPreviewsFinished] = useState(0);
    const [rubricAbortController, setRubricAbortController] = useState(null);
    const [previewAbortControllers, setPreviewAbortControllers] = useState([]);
    const [tempSubmissionIds, setTempSubmissionIds] = useState([]);
    const [tempSubmissions, setTempSubmissions] = useState([]);

    // form state
    const [isDirty, setIsDirty] = useState(false);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const closeFineTuneDialog = useCallback(() => {
        setFineTuneDialog((current) => ({ ...current, open: false }));
        setTimeout(() => {
            setFineTuneDialog((current) => ({ ...current, expanded: 'rubric' }));
            setIsRubricGenerating(false);
            setIsPreviewGenerating(false);
            setIsDirty(false);
        }, 150);
    }, [setFineTuneDialog]);

    const handleClose = () => {
        if (!isDirty) return closeFineTuneDialog();
        setDialog({
            open: true,
            message: t('dialog.unsaved-fine-tune-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'dialog.unsaved-fine-tune-changes.description',
                'If you exit without regenerating grades/feedback your changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                closeFineTuneDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleChangeAccordion = (key) => {
        setFineTuneDialog((current) => ({ ...current, expanded: key }));
    };

    const handleSort = useCallback((value) => {
        setIsDirty(true);
        setRubric((current) => {
            const newCriteria = cloneDeep(current.criteria).map((criterion) => {
                criterion.options.sort((a, b) => {
                    const numA = Number(a.rate);
                    const numB = Number(b.rate);
                    if (!numA && !numB) return 0;
                    if (a.rate === '') return 1;
                    if (b.rate === '') return -1;
                    if (value === 'asc') return numA - numB;
                    return numB - numA;
                });
                return criterion;
            });
            return { ...current, criteria: newCriteria, sort: value };
        });
    }, []);

    const handleChange = useCallback((criterionId, optionId, key, value) => {
        setIsDirty(true);
        setRubric((current) => {
            return {
                ...current,
                criteria: current.criteria.map((criterion) => {
                    if (criterion.id !== criterionId) return criterion;
                    return {
                        ...criterion,
                        options: criterion.options.map((option) => {
                            if (option.id !== optionId) return option;
                            return {
                                ...option,
                                [key]: value,
                            };
                        }),
                    };
                }),
            };
        });
    }, []);

    const handleClickAddCriterion = useCallback(() => {
        setCreateCriterionDialog({ open: true });
    }, []);

    const handleClickEditCriterion = useCallback(
        (criterionId) => {
            const { id, title, description } = rubric.criteria.find(({ id }) => id === criterionId);
            setEditCriterionDialog({ open: true, id, title, description });
        },
        [rubric.criteria]
    );

    const handleClickDuplicateCriterion = useCallback(
        (criterionId) => {
            setIsDirty(true);
            const criterionToDuplicate = rubric.criteria.find(({ id }) => id === criterionId);
            setRubric((current) => {
                return {
                    ...current,
                    criteria: [
                        ...current.criteria,
                        {
                            ...criterionToDuplicate,
                            id: uuid(),
                            options: criterionToDuplicate.options.map((option) => ({ ...option, id: uuid() })),
                        },
                    ],
                };
            });
        },
        [rubric.criteria]
    );

    const handleClickAddOption = useCallback(
        (criterionId, index) => {
            setIsDirty(true);
            setRubric((current) => {
                return {
                    ...current,
                    criteria: current.criteria.map((criterion) => {
                        if (criterion.id !== criterionId) return criterion;
                        if (criterion.options.length === index) {
                            const el = document.getElementById(`${criterion.id}-stack`);
                            setTimeout(() => {
                                el.scrollLeft = el.scrollWidth;
                            }, 0);
                        }
                        return {
                            ...criterion,
                            options: insertAtIndex(criterion.options, index, {
                                id: uuid(),
                                title: '',
                                description: '',
                                rate: isUnscored ? null : '',
                            }),
                        };
                    }),
                };
            });
        },
        [isUnscored]
    );

    const handleClickDeleteOption = useCallback((criterionId, optionId) => {
        setIsDirty(true);
        setRubric((current) => {
            return {
                ...current,
                criteria: current.criteria.map((criterion) => {
                    if (criterion.id !== criterionId) return criterion;
                    return {
                        ...criterion,
                        options: criterion.options.filter(({ id }) => id !== optionId),
                    };
                }),
            };
        });
        setErrors((current) => ({
            ...current,
            criteria: {
                ...(current.criteria || {}),
                [criterionId]: omit(current.criteria?.[criterionId], [optionId]),
            },
        }));
    }, []);

    const handleDragEnd = useCallback(({ draggableId, source, destination }) => {
        if (!destination) return;
        const srcIndex = source.index;
        const dstIndex = destination.index;
        if (srcIndex === dstIndex) return;
        setIsDirty(true);
        setRubric((current) => {
            const newCriteria = cloneDeep(current.criteria);
            newCriteria.splice(srcIndex, 1);
            newCriteria.splice(
                dstIndex,
                0,
                current.criteria.find(({ id }) => id === draggableId)
            );
            return {
                ...current,
                criteria: newCriteria,
            };
        });
    }, []);

    const addCriterion = (title, description) => {
        setIsDirty(true);
        setRubric((current) => {
            return {
                ...current,
                criteria: [
                    ...current.criteria,
                    {
                        id: uuid(),
                        title,
                        description,
                        options: [
                            {
                                id: uuid(),
                                title: '',
                                description: '',
                                rate: isUnscored ? null : '',
                            },
                            {
                                id: uuid(),
                                title: '',
                                description: '',
                                rate: isUnscored ? null : '',
                            },
                        ],
                    },
                ],
            };
        });
        setCreateCriterionDialog({ open: false });
    };

    const updateCriterion = (criterionId, title, description) => {
        setIsDirty(true);
        setRubric((current) => ({
            ...current,
            criteria: current.criteria.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                return {
                    ...criterion,
                    title,
                    description,
                };
            }),
        }));
        setEditCriterionDialog((current) => ({ ...current, open: false }));
    };

    const deleteCriterion = (criterionId) => {
        setIsDirty(true);
        setRubric((current) => ({
            ...current,
            criteria: current.criteria.filter(({ id }) => id !== criterionId),
        }));
        setErrors((current) => ({ ...current, criteria: omit(current.criteria, [criterionId]) }));
        setEditCriterionDialog((current) => ({ ...current, open: false }));
    };

    const validateCriteria = useCallback(() => {
        let error = false;
        rubric.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isUnscored && !isValidRate(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [isUnscored, rubric.criteria, t]);

    const handleClickGenerateRubric = async () => {
        if (rubricAbortController) rubricAbortController.abort();

        const controller = new AbortController();
        setRubricAbortController(controller);
        setIsRubricGenerating(true);

        try {
            const response = await getChatCompletion(
                AI_RUBRIC_MODIFICATION_URL,
                JSON.stringify({
                    rubric: {
                        title: '',
                        ...rubric,
                    },
                    modification_instructions: prompt,
                }),
                controller.signal
            );
            const result = await response.json();
            const newCriteria = cloneDeep(result.criteria).map((criterion) => {
                if (!isUnscored) {
                    criterion.options.sort((a, b) => {
                        const numA = Number(a.rate);
                        const numB = Number(b.rate);
                        if (!numA && !numB) return 0;
                        if (a.rate === '') return 1;
                        if (b.rate === '') return -1;
                        if (rubric.sort === 'asc') return numA - numB;
                        return numB - numA;
                    });
                }
                return criterion;
            });
            setPrompt('');
            const regeneratedCriteria = newCriteria.map((criterion) => ({
                ...criterion,
                id: uuid(),
                options: criterion.options.map((option) => ({
                    ...option,
                    id: uuid(),
                    rate: isUnscored ? null : option.rate,
                })),
            }));
            setRubric((current) => ({
                ...current,
                criteria: isUnscored ? normalizeRubricCriteriaLevels(regeneratedCriteria) : regeneratedCriteria,
            }));
            message.success(t('message.rubric-updated-success', 'Your rubric was updated successfully.'));
        } catch (e) {
            if (e.name === 'AbortError') return;
            console.error(e);
            message.error(t('error.prompt', 'An error occurred. Please review your prompt and try again.'));
        } finally {
            setIsRubricGenerating(false);
        }
    };

    const handleClickCancelRubricGeneration = () => {
        setDialog({
            open: true,
            message: t('dialog.cancel-rubric-enhancement.title', 'Cancel Rubric Enhancement?'),
            description: t(
                'dialog.cancel-rubric-enhancement.description',
                'If you leave now, your rubric will remain unchanged.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                rubricAbortController?.abort();
                setRubricAbortController(null);
                setIsRubricGenerating(false);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickRegenerate = async () => {
        setHasClickedSubmit(true);
        const isValid = validateCriteria();
        if (!isValid || !rubric.criteria.length)
            return message.error(t('error.invalid-rubric', 'The rubric is not valid.'));

        previewAbortControllers.forEach((controller) => controller.abort());
        const controllers = previewSubmissions.map(() => new AbortController());
        setPreviewAbortControllers(controllers);
        const tempSubmissionIds = previewSubmissions.map(() => uuid());
        setTempSubmissionIds(tempSubmissionIds);
        setIsPreviewGenerating(true);

        try {
            const account = await getAccount();
            await Promise.all(
                previewSubmissions.map(async (previewSubmission) => {
                    const assignmentPayload = {
                        ...currentAssignment,
                        rubric: {
                            ...currentAssignment.rubric,
                            criteria: isUnscored ? normalizeRubricCriteriaLevels(rubric.criteria) : rubric.criteria,
                        },
                    };
                    await getAIPreview({
                        target: 'both',
                        course_id: course.id,
                        owner_account_id: currentAssignment.owner_account_id,
                        assignment: assignmentPayload,
                        submission: previewSubmission,
                        connections: account.connections.map(({ id }) => id),
                        language: course.language ?? 'English',
                    });
                })
            );
        } catch (e) {
            if (e.name === 'AbortError') return;
            console.error(e);
            message.error(
                t('common:message.something-went-wrong-try-again', 'Something went wrong. Please try again.')
            );
        }
    };

    const handleClickCancelRegeneration = () => {
        setDialog({
            open: true,
            message: t('dialog.cancel-regeneration.title', 'Cancel Regeneration?'),
            description: t(
                'dialog.cancel-regeneration.description',
                'If you leave now, grades/feedback will not be regenerated.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                previewAbortControllers.forEach((controller) => controller.abort());
                setPreviewAbortControllers([]);
                setTempSubmissionIds([]);
                setIsPreviewGenerating(false);
                await Promise.all(
                    tempSubmissionIds.map(async (submissionId) => {
                        await cancelPreview({ currentAssignment, submissionId });
                    })
                );
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    useEffect(() => {
        if (hasClickedSubmit) validateCriteria();
    }, [hasClickedSubmit, validateCriteria]);

    useEffect(() => {
        if (fineTuneDialog.open) {
            const changes = fineTuneDialog.changes;
            if (changes) {
                setRubric(changes.rubric);
                setErrors(defaultErrors);
                setGradingInstructions(changes.ai_grading_context);
                setFeedbackInstructions(changes.ai_feedback_context);
            } else {
                setRubric({
                    criteria: currentAssignment.rubric.criteria,
                    scoring_mode: currentAssignment.rubric.scoring_mode,
                    sort: currentAssignment.rubric.sort,
                });
                setErrors(defaultErrors);
                setGradingInstructions(currentAssignment.ai_grading_context);
                setFeedbackInstructions(currentAssignment.ai_feedback_context);
            }
        } else {
            setPrompt('');
        }

        return () => {
            rubricAbortController?.abort();
            previewAbortControllers.forEach((controller) => controller.abort());
        };
    }, [
        currentAssignment.ai_feedback_context,
        currentAssignment.ai_grading_context,
        currentAssignment.rubric.criteria,
        currentAssignment.rubric.scoring_mode,
        currentAssignment.rubric.sort,
        fineTuneDialog.changes,
        fineTuneDialog.open,
        previewAbortControllers,
        rubricAbortController,
    ]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.data?.payload_type === 'regenerate_submission' && fineTuneDialog.open) {
                setTempSubmissions((current) => {
                    return [...current, message.data.payload.submission];
                });
                setNumPreviewsFinished((current) => current + 1);
            }
        });
        return cleanup;
    }, [fineTuneDialog.open, onMessage]);

    useEffect(() => {
        if (tempSubmissions.length && tempSubmissions.length === tempSubmissionIds.length) {
            setForm({ criteria: {}, feedback: null, inline_comments: null, grading_explanations: {} });
            setPreviewSubmissions((current) => {
                return current.map((ps) => {
                    const targetResult = tempSubmissions.find(({ files }) =>
                        files.find((f) => f.key === ps.files[0].key)
                    );
                    return omit(
                        {
                            ...ps,
                            ai: targetResult?.data,
                        },
                        ['grading']
                    );
                });
            });
            setFineTuneDialog((current) => ({
                ...current,
                changes: {
                    rubric: tempSubmissions[0].assignment_data.rubric,
                    ai_grading_context: gradingInstructions,
                    ai_feedback_context: feedbackInstructions,
                },
            }));
            setIsPreviewGenerating(false);
            setTempSubmissions([]);
            setNumPreviewsFinished(0);
            closeFineTuneDialog();
        }
    }, [
        closeFineTuneDialog,
        feedbackInstructions,
        gradingInstructions,
        setFineTuneDialog,
        setForm,
        setPreviewSubmissions,
        tempSubmissionIds.length,
        tempSubmissions,
        rubric,
    ]);

    return (
        <>
            <CustomResponsiveDialog device={device} open={fineTuneDialog.open} scroll={'paper'} onClose={handleClose}>
                <DialogTitle>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            {t('fine-tuning.fine-tune-assignment-details', 'Fine-Tune Assignment Details')}
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            disabled={isPreviewGenerating}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    </Stack>
                </DialogTitle>
                <Divider />
                {isRubricGenerating ? (
                    <DialogContent>
                        <Stack sx={{ marginTop: '25vh' }} spacing={8}>
                            <Stack alignItems={'center'} spacing={2}>
                                <Text component={'p'} variant={'h6'} weight={'semibold'}>
                                    {t('rubric.wait-enhance-content', 'Please wait while your rubric is enhanced')}
                                </Text>
                                <Text sx={{ maxWidth: '384px' }} align={'center'} color={'light'}>
                                    {t(
                                        'keep-window-open',
                                        'Please keep this window open until this process completes. This may take a few minutes.'
                                    )}
                                </Text>
                            </Stack>
                            <Box sx={{ height: '40px', transform: 'scale(0.5)' }}>
                                <Loading />
                            </Box>
                            <Button
                                sx={{ alignSelf: 'center' }}
                                color={'neutral'}
                                variant={'tertiary'}
                                onClick={handleClickCancelRubricGeneration}
                            >
                                {t('common:controls.cancel', 'Cancel')}
                            </Button>
                        </Stack>
                    </DialogContent>
                ) : isPreviewGenerating ? (
                    <DialogContent>
                        <Button
                            startIcon={<Icon kind={'arrowLeft'} />}
                            variant={'borderless'}
                            onClick={handleClickCancelRegeneration}
                        >
                            {t('common:controls.back', 'Back')}
                        </Button>
                        <Stack sx={{ margin: 'auto', marginTop: '25vh', maxWidth: '384px' }} spacing={8}>
                            <Stack spacing={2}>
                                <Text
                                    sx={{ mx: 2 }}
                                    align={'center'}
                                    component={'p'}
                                    variant={'sh1'}
                                    weight={'semibold'}
                                >
                                    {t(
                                        'fine-tuning.regenerate-title',
                                        'A preview is being generated based on your updated assignment data'
                                    )}
                                </Text>
                                <Text sx={{ mx: 4 }} align={'center'} color={'light'}>
                                    {t(
                                        'fine-tuning.regenerate-description',
                                        'Please keep this window open until this process completes. This will take about 3-5 minutes.'
                                    )}
                                </Text>
                            </Stack>
                            <Stack sx={{ width: '100%' }} spacing={2}>
                                <Text color={'light'}>
                                    {numPreviewsFinished === 0
                                        ? t('fine-tuning.preparing-submissions', 'Preparing submissions...')
                                        : t('fine-tuning.submissions-processed', '{{count}} submissions processed', {
                                              count: `${numPreviewsFinished}/${previewSubmissions.length}`,
                                          })}
                                </Text>
                                <ProgressBar
                                    aria-label={'preview submission regeneration progress'}
                                    simulateProgress={{
                                        duration: 30,
                                        maxValue: 90,
                                    }}
                                    value={Number(((numPreviewsFinished / previewSubmissions.length) * 100).toFixed(0))}
                                />
                            </Stack>
                        </Stack>
                    </DialogContent>
                ) : (
                    <CustomDialogContent>
                        <Stack spacing={5}>
                            <Text>
                                {t(
                                    'fine-tuning.update-core-details-description',
                                    'If you need to update core assignment details, you can make changes here. Once you’re happy with the updates, regenerate grades and feedback to preview the revised results. If you’re not satisfied, exit without applying the changes.'
                                )}
                            </Text>
                            <Paper variant={'outlined'}>
                                <Accordion
                                    expanded={fineTuneDialog.expanded === 'rubric'}
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    onChange={() => handleChangeAccordion('rubric')}
                                >
                                    <AccordionSummary>
                                        <Text variant={'sh2'} weight={'semibold'}>
                                            {t('rubric.title-rubric', 'Rubric')}
                                        </Text>
                                    </AccordionSummary>
                                    <CustomAccordionDetails>
                                        {currentAssignment.ai_feedback_settings.structure === 'templated' ? (
                                            <Text color={'light'}>
                                                {t(
                                                    'fine-tuning.smart-feedback-disclaimer',
                                                    'Your assignment is currently using Smart Feedback Templates, which can’t be fine-tuned here. To make changes, please edit the rubric in the main assignment details.'
                                                )}
                                            </Text>
                                        ) : (
                                            <Stack spacing={5}>
                                                <Stack
                                                    sx={(theme) => ({
                                                        borderRadius: '12px',
                                                        padding: theme.spacing(3),
                                                        background: theme.palette.ensightful.primary[50],
                                                    })}
                                                    spacing={3}
                                                >
                                                    <TextArea
                                                        label={t(
                                                            'fine-tuning.improvement-label',
                                                            'What do you want to improve?'
                                                        )}
                                                        placeholder={t(
                                                            'fine-tuning.improvement-placeholder',
                                                            'Ex. Quantify ratings where possible'
                                                        )}
                                                        rows={6}
                                                        value={prompt}
                                                        onChange={(e) => {
                                                            setIsDirty(true);
                                                            setPrompt(e.target.value);
                                                        }}
                                                    />
                                                    <Button
                                                        sx={device === 'mobile' ? undefined : { alignSelf: 'flex-end' }}
                                                        startIcon={<Icon kind={'ai'} />}
                                                        variant={'secondary'}
                                                        onClick={handleClickGenerateRubric}
                                                    >
                                                        {t('common:controls.enhance-with-ai', 'Enhance with AI')}
                                                    </Button>
                                                </Stack>
                                                <RubricTable
                                                    type={'create'}
                                                    data={rubric.criteria}
                                                    defaultExpanded={false}
                                                    errors={errors.criteria}
                                                    isUnscored={isUnscored}
                                                    sort={rubric.sort}
                                                    setSort={handleSort}
                                                    onChange={handleChange}
                                                    onClickAddCriterion={handleClickAddCriterion}
                                                    onClickEditCriterion={handleClickEditCriterion}
                                                    onClickDuplicateCriterion={handleClickDuplicateCriterion}
                                                    onClickAddOption={handleClickAddOption}
                                                    onClickDeleteOption={handleClickDeleteOption}
                                                    onDragEnd={handleDragEnd}
                                                />
                                            </Stack>
                                        )}
                                    </CustomAccordionDetails>
                                </Accordion>
                            </Paper>
                            <Paper variant={'outlined'}>
                                <Accordion
                                    expanded={fineTuneDialog.expanded === 'grading'}
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    onChange={() => handleChangeAccordion('grading')}
                                >
                                    <AccordionSummary>
                                        <Text variant={'sh2'} weight={'semibold'}>
                                            {t('advanced-grading.grading-instructions-label', 'Grading Instructions')}
                                        </Text>
                                    </AccordionSummary>
                                    <CustomAccordionDetails>
                                        {currentAssignment.ai_grading_enabled ? (
                                            <RichTextArea
                                                error={
                                                    getHTMLWordCount(gradingInstructions) >
                                                    CUSTOM_CONTENT_WORD_COUNT_LIMIT
                                                }
                                                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                                    wordLimitStatus: `${getHTMLWordCount(
                                                        gradingInstructions
                                                    )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                                })}
                                                helperTextPosition={'end'}
                                                label={t(
                                                    'advanced-grading.grading-instructions-label',
                                                    'Grading Instructions'
                                                )}
                                                placeholder={t(
                                                    'advanced-grading.grading-suggestions-placeholder',
                                                    'Do you have any additional instructions about grading suggestions?'
                                                )}
                                                value={gradingInstructions}
                                                onChange={(value) => {
                                                    setIsDirty(true);
                                                    setGradingInstructions(value);
                                                }}
                                            />
                                        ) : (
                                            <Text color={'light'}>
                                                {t(
                                                    'advanced-grading.grading-not-enabled',
                                                    'Grading is not enabled for this assignment. To enable it, please update the main assignment setup.'
                                                )}
                                            </Text>
                                        )}
                                    </CustomAccordionDetails>
                                </Accordion>
                            </Paper>
                            <Paper variant={'outlined'}>
                                <Accordion
                                    expanded={fineTuneDialog.expanded === 'feedback'}
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    onChange={() => handleChangeAccordion('feedback')}
                                >
                                    <AccordionSummary>
                                        <Text variant={'sh2'} weight={'semibold'}>
                                            {t('feedback-section.instructions-label', 'Feedback Instructions')}
                                        </Text>
                                    </AccordionSummary>
                                    <CustomAccordionDetails>
                                        {currentAssignment.ai_feedback_settings.structure === 'templated' ? (
                                            <Text color={'light'}>
                                                {t(
                                                    'fine-tuning.smart-feedback-disclaimer',
                                                    'Your assignment is currently using Smart Feedback Templates, which can’t be fine-tuned here. To make changes to these templates, please edit them in the main assignment details.'
                                                )}
                                            </Text>
                                        ) : currentAssignment.ai_feedback_enabled ? (
                                            <RichTextArea
                                                error={
                                                    getHTMLWordCount(feedbackInstructions) >
                                                    CUSTOM_CONTENT_WORD_COUNT_LIMIT
                                                }
                                                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                                    wordLimitStatus: `${getHTMLWordCount(
                                                        feedbackInstructions
                                                    )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                                })}
                                                helperTextPosition={'end'}
                                                label={t(
                                                    'feedback-settings.additional-instruction.label',
                                                    'Additional Instructions for the AI'
                                                )}
                                                placeholder={t(
                                                    'feedback-section.settings.instruction-placeholder',
                                                    'Provide instructions about the structure, tone and content of feedback here. '
                                                )}
                                                value={feedbackInstructions}
                                                onChange={(value) => {
                                                    setIsDirty(true);
                                                    setFeedbackInstructions(value);
                                                }}
                                            />
                                        ) : (
                                            <Text color={'light'}>
                                                {t(
                                                    'feedback-section.feedback-not-enabled',
                                                    'Feedback is not enabled for this assignment. To enable it, please update the main assignment setup.'
                                                )}
                                            </Text>
                                        )}
                                    </CustomAccordionDetails>
                                </Accordion>
                            </Paper>
                        </Stack>
                    </CustomDialogContent>
                )}
                <Divider />
                <DialogActions>
                    <Button disabled={isRubricGenerating || isPreviewGenerating} onClick={handleClickRegenerate}>
                        {t('regenerate-grades-feedback', 'Regenerate Grades/Feedback')}
                    </Button>
                </DialogActions>
            </CustomResponsiveDialog>
            <AddCriterionDialog
                open={createCriterionDialog.open}
                onClose={() => setCreateCriterionDialog({ open: false })}
                onSave={addCriterion}
            />
            <EditCriterionDialog
                open={editCriterionDialog.open}
                criterion={{
                    id: editCriterionDialog.id,
                    title: editCriterionDialog.title,
                    description: editCriterionDialog.description,
                }}
                onClose={() => setEditCriterionDialog((current) => ({ ...current, open: false }))}
                onSave={updateCriterion}
                onDelete={deleteCriterion}
            />
        </>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ theme, device }) => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
        ...(device === 'mobile' && {
            zIndex: theme.zIndex.drawer + 1,
        }),
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(5),
    };
});

export default FineTuneDialog;
